<template>
  <Card>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium text-text">Usage Statistics</h3>
      <div class="flex space-x-2">
        <select
          v-model="selectedPeriod"
          @change="fetchUsageData"
          class="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
        <Button variant="outline" size="sm" @click="fetchUsageData" :loading="isLoading">
          <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </Button>
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <LoadingSpinner />
    </div>

    <div v-else-if="error" class="text-center py-8">
      <Alert variant="error" :message="error" />
    </div>

    <div v-else-if="usageData">
      <!-- Summary Stats -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="text-sm font-medium text-gray-500">Total Requests</div>
          <div class="text-2xl font-bold text-gray-900">
            {{ formatNumber(usageData.summary.total_requests) }}
          </div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="text-sm font-medium text-gray-500">Last 24h</div>
          <div class="text-2xl font-bold text-gray-900">
            {{ formatNumber(usageData.summary.requests_last_24h) }}
          </div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="text-sm font-medium text-gray-500">Success Rate</div>
          <div class="text-2xl font-bold text-green-600">{{ calculateSuccessRate() }}%</div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="text-sm font-medium text-gray-500">Avg/Day</div>
          <div class="text-2xl font-bold text-gray-900">
            {{ formatNumber(usageData.summary.avg_requests_per_day) }}
          </div>
        </div>
      </div>

      <!-- Timeline Chart -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Request Timeline</h4>
        <div class="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <!-- Simplified chart representation -->
          <div class="text-gray-500 text-sm">
            📊 Chart visualization would be implemented here
            <br />
            <span class="text-xs">Consider using Chart.js or similar library</span>
          </div>
        </div>
      </div>

      <!-- Status Code Distribution -->
      <div v-if="hasStatusCodes || hasEndpoints" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 class="text-sm font-medium text-gray-700 mb-3">Status Code Distribution</h4>
          <div v-if="hasStatusCodes" class="space-y-2">
            <div
              v-for="(count, status) in usageData.status_codes"
              :key="status"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <span
                  :class="getStatusCodeColor(status)"
                  class="inline-block w-3 h-3 rounded-full mr-2"
                ></span>
                <span class="text-sm text-gray-600">{{ status }}</span>
              </div>
              <div class="text-sm font-medium text-gray-900">
                {{ formatNumber(count) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Top Endpoints -->
        <div v-if="hasEndpoints">
          <h4 class="text-sm font-medium text-gray-700 mb-3">Top Endpoints</h4>
          <div class="space-y-2">
            <div
              v-for="endpoint in usageData.endpoints.slice(0, 5)"
              :key="`${endpoint.method}-${endpoint.path}`"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <span
                  :class="getMethodColor(endpoint.method)"
                  class="inline-block px-2 py-1 rounded text-xs font-medium mr-2"
                >
                  {{ endpoint.method }}
                </span>
                <span class="text-sm text-gray-600 truncate">{{ endpoint.path }}</span>
              </div>
              <div class="text-sm font-medium text-gray-900">
                {{ endpoint.percentage.toFixed(1) }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Geographic Distribution -->
      <div v-if="hasGeography" class="mt-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Geographic Distribution</h4>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div v-for="(count, region) in usageData.geography" :key="region" class="text-center">
            <div class="text-lg font-bold text-gray-900">{{ formatNumber(count) }}</div>
            <div class="text-sm text-gray-500">{{ region }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-8 text-gray-500">
      {{ keyId ? 'No usage data available' : 'Select an API key to view analytics' }}
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { Button, LoadingSpinner, Alert, Card, useNotification } from '@asabdev/asab-ui';
  import { analyticsApi } from '@/lib/api/analytics';

  interface UsageEndpoint {
    method: string;
    path: string;
    percentage: number;
  }

  interface UsageSummary {
    total_requests: number;
    requests_last_24h: number;
    requests_last_7d: number;
    requests_last_30d: number;
    requests_last_90d: number;
    avg_requests_per_day: number;
    peak_requests_per_hour: number;
  }

  interface UsageData {
    summary: UsageSummary;
    timeline: unknown[];
    status_codes: Record<string, number>;
    endpoints: UsageEndpoint[];
    geography?: Record<string, number>;
  }

  interface Props {
    keyId: string;
  }

  const props = defineProps<Props>();

  const selectedPeriod = ref('30d');
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const usageData = ref<UsageData | null>(null);
  const { showError } = useNotification();
  const hasKey = computed(() => props.keyId.trim().length > 0);

  type UsageByApiKeyItem = {
    key_id?: string;
    requests?: number;
    tokens?: number;
  };

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
  };

  const parseUsageByApiKey = (
    data: unknown,
    keyId: string
  ): { requests: number; tokens: number } | null => {
    if (!isRecord(data)) return null;
    const keys = data.keys;
    if (!Array.isArray(keys)) return null;
    const match = keys.find(item => isRecord(item) && item.key_id === keyId) as
      | UsageByApiKeyItem
      | undefined;
    if (!match) return null;
    return {
      requests: typeof match.requests === 'number' ? match.requests : 0,
      tokens: typeof match.tokens === 'number' ? match.tokens : 0,
    };
  };

  const getDateRange = (period: string): { start: string; end: string; days: number } => {
    const end = new Date();
    let days = 30;
    if (period === '24h') days = 1;
    if (period === '7d') days = 7;
    if (period === '90d') days = 90;
    const start = new Date(end.getTime());
    start.setDate(end.getDate() - days);
    return { start: start.toISOString(), end: end.toISOString(), days };
  };

  const fetchUsageData = async (): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;
      if (!hasKey.value) {
        usageData.value = null;
        return;
      }

      const { start, end, days } = getDateRange(selectedPeriod.value);
      const response = await analyticsApi.getUsageByApiKey({ start, end });
      const usage = parseUsageByApiKey(response, props.keyId);
      if (!usage) {
        usageData.value = null;
        error.value = 'No usage data available for the selected API key.';
        return;
      }

      const requests = usage.requests;
      usageData.value = {
        summary: {
          total_requests: requests,
          requests_last_24h: selectedPeriod.value === '24h' ? requests : 0,
          requests_last_7d: selectedPeriod.value === '7d' ? requests : 0,
          requests_last_30d: selectedPeriod.value === '30d' ? requests : 0,
          requests_last_90d: selectedPeriod.value === '90d' ? requests : 0,
          avg_requests_per_day: days > 0 ? requests / days : 0,
          peak_requests_per_hour: 0,
        },
        timeline: [],
        status_codes: {},
        endpoints: [],
      };
    } catch (err) {
      const message =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to fetch usage data';
      error.value = message;
      showError(message);
    } finally {
      isLoading.value = false;
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const calculateSuccessRate = (): string => {
    if (!usageData.value?.status_codes) return '0';

    const total = Object.values(usageData.value.status_codes).reduce(
      (sum, count) => sum + count,
      0
    );
    const successful =
      (usageData.value.status_codes['200'] || 0) +
      (usageData.value.status_codes['201'] || 0) +
      (usageData.value.status_codes['204'] || 0);

    return total > 0 ? ((successful / total) * 100).toFixed(1) : '0';
  };

  const hasStatusCodes = computed(() => {
    return Object.keys(usageData.value?.status_codes ?? {}).length > 0;
  });

  const hasEndpoints = computed(() => {
    return (usageData.value?.endpoints ?? []).length > 0;
  });

  const hasGeography = computed(() => {
    return Object.keys(usageData.value?.geography ?? {}).length > 0;
  });

  const getStatusCodeColor = (status: string): string => {
    const code = parseInt(status);
    if (code >= 200 && code < 300) return 'bg-green-500';
    if (code >= 300 && code < 400) return 'bg-blue-500';
    if (code >= 400 && code < 500) return 'bg-yellow-500';
    if (code >= 500) return 'bg-red-500';
    return 'bg-gray-500';
  };

  const getMethodColor = (method: string): string => {
    switch (method.toLowerCase()) {
      case 'get':
        return 'bg-green-100 text-green-800';
      case 'post':
        return 'bg-blue-100 text-blue-800';
      case 'put':
        return 'bg-yellow-100 text-yellow-800';
      case 'patch':
        return 'bg-orange-100 text-orange-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  watch(
    [() => props.keyId, selectedPeriod],
    () => {
      void fetchUsageData();
    },
    { immediate: true }
  );

</script>
